// src/pages/fleet/FleetAlertsPage.tsx — trip alerts (overspeed, route deviation).
import React, { useEffect, useRef, useState } from 'react';
import { apiError } from '../../utils/errors';
import { getFleetAlerts, resolveFleetAlert } from '../../services/fleetApi';
import { P } from './fleetStyles';
import type { TripAlert } from '../../types';

const SEV_COLOR: Record<string, string> = { HIGH: '#DC3545', MEDIUM: '#E8941A', LOW: '#6B7280' };

export default function FleetAlertsPage() {
  const [alerts, setAlerts] = useState<TripAlert[]>([]);
  const [filter, setFilter] = useState<string>('false'); // unresolved by default
  const [loading, setLoading] = useState<boolean>(true);

  const [err, setErr] = useState<string>('');
  const seq = useRef(0);   // newest request wins when the filter is switched quickly

  const load = () => {
    const mine = ++seq.current;
    setLoading(true); setErr('');
    const params = filter === 'all' ? {} : { resolved: filter };
    getFleetAlerts(params)
      .then(r => { if (mine === seq.current) setAlerts(r.data.alerts || []); })
      .catch(() => { if (mine === seq.current) { setAlerts([]); setErr('Could not load alerts. Please try again.'); } })
      .finally(() => { if (mine === seq.current) setLoading(false); });
  };
  useEffect(load, [filter]);

  const resolve = async (id: string) => {
    try { await resolveFleetAlert(id); load(); }
    catch (e) { setErr(apiError(e, 'Could not resolve this alert. Please try again.')); }
  };

  return (
    <div style={P.page}>
      <div style={P.head}><div><h1 style={P.h1}>🚨 Fleet Alerts</h1><p style={P.sub}>Overspeed, GPS gap, delayed data, wrong coordinates, source failures and route deviation events</p></div></div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        {[['false', 'Unresolved'], ['true', 'Resolved'], ['all', 'All']].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ ...P.chip, ...(filter === v ? P.chipOn : {}) }}>{l}</button>
        ))}
      </div>

      <div style={{ ...P.card, overflowX: 'auto' }}>
        <table style={P.table}>
          <thead><tr>{['Severity', 'Type', 'Source', 'Message', 'Vehicle / Trip', 'Time', ''].map(h => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
          <tbody>
            {alerts.map(a => (
              <tr key={a.id}>
                <td style={P.td}><span style={{ ...P.badge, background: SEV_COLOR[a.severity] || '#6B7280' }}>{a.severity}</span></td>
                <td style={P.td}>{a.type}</td>
                <td style={P.td}>{a.source || 'DEVICE'}</td>
                <td style={P.td}>{a.message}</td>
                <td style={P.td}>{a.vehicle?.vehicleNumber || a.trip?.vehicle?.vehicleNumber || '—'}<div style={P.muted}>{a.trip?.tripNumber || ''}</div></td>
                <td style={P.td}>{new Date(a.createdAt ?? NaN).toLocaleString()}</td>
                <td style={P.td}>{a.isResolved
                  ? <span style={{ color: '#22A559', fontSize: 12, fontWeight: 600 }}>✓ Resolved</span>
                  : <button style={P.btnGhost} onClick={() => resolve(a.id)}>Resolve</button>}</td>
              </tr>
            ))}
            {err && <tr><td colSpan={7} style={{ ...P.emptyTd, color: 'var(--fx-red)' }}>{err}</td></tr>}
            {!loading && !err && alerts.length === 0 && <tr><td colSpan={7} style={P.emptyTd}>No alerts</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
